import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { BRAND_SLUGS, BRANDS, logoUrl } from "../lib/brands";
import { brandPath } from "../lib/seo";

export default function BrandStrip() {
  const { t, lang } = useApp();
  const ref = useRef(null);
  const controlLabels = lang === "TR"
    ? { next: "Sonraki", previous: "Önceki" }
    : lang === "RU"
      ? { next: "Следующий", previous: "Предыдущий" }
      : { next: "Next", previous: "Previous" };
  const scroll = (dir) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: dir * ref.current.clientWidth * 0.85,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="py-16 border-b border-black/10" data-testid="brand-strip">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center mb-8">
        <h2 className="font-serif-lux font-bold text-3xl sm:text-4xl text-slate-900">{t.home.brandTitle}</h2>
        <p className="mt-3 text-slate-600 text-sm sm:text-base">{t.home.brandSub}</p>
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <button
          type="button"
          onClick={() => scroll(-1)}
          data-testid="brand-prev"
          aria-label={controlLabels.previous}
          className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-md transition-[border-color,color] duration-200 hover:border-teal-500/40 hover:text-teal-600 sm:left-4"
        >
          <ChevronLeft size={20} />
        </button>

        <div ref={ref} className="no-scrollbar flex gap-4 overflow-x-auto snap-x snap-mandatory px-12">
          {BRAND_SLUGS.map((slug) => {
            const b = BRANDS[slug];
            return (
              <Link
                key={slug}
                to={brandPath(slug)}
                data-testid={`brand-${slug}`}
                className="group snap-start flex shrink-0 basis-1/2 flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-6 transition-[border-color,box-shadow] duration-300 hover:border-teal-500/40 hover:shadow-md sm:basis-1/3 md:basis-1/4 lg:basis-1/6"
              >
                <img
                  src={logoUrl(slug)}
                  alt={`${b.name} logo`}
                  width="160"
                  height="64"
                  loading="lazy"
                  decoding="async"
                  title={b.name}
                  className="h-11 w-auto object-contain opacity-80 transition-[opacity,transform] duration-300 group-hover:scale-110 group-hover:opacity-100 sm:h-12"
                />
                <span className="text-center font-mono-jb text-[11px] uppercase tracking-[0.18em] text-teal-800 transition-colors duration-300 group-hover:text-teal-900 sm:text-xs">
                  {b.name}
                </span>
              </Link>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => scroll(1)}
          data-testid="brand-next"
          aria-label={controlLabels.next}
          className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-md transition-[border-color,color] duration-200 hover:border-teal-500/40 hover:text-teal-600 sm:right-4"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </section>
  );
}
